import { annotationLegendSymbol } from '../../annotations/legend';
import { AnnotationLegendSymbolPreview } from './style/SymbolPreview';
import { useRef, useState } from 'react';

import type { AnnotationLayer } from '../../annotations/model';
import { ConfirmationDialog } from '../../components/ConfirmationDialog';
import { Icon } from '../../components/Icon';
import { AnchoredMenu } from './AnchoredMenu';
import { focusAfterListItemRemoval } from './focusAfterRemoval';

export function AnnotationLayerRow({
  layer,
  selected,
  authoring,
  first,
  last,
  onSelect,
  onToggleVisibility,
  onRename,
  onRemove,
  onMove,
}: {
  readonly layer: AnnotationLayer;
  readonly selected: boolean;
  readonly authoring: boolean;
  readonly first: boolean;
  readonly last: boolean;
  readonly onSelect: () => void;
  readonly onToggleVisibility: () => void;
  readonly onRename: (name: string) => boolean;
  readonly onRemove: () => void;
  readonly onMove: (direction: -1 | 1) => void;
}) {
  const preview = layer.annotations[0] ? annotationLegendSymbol(layer.annotations[0]) : null;
  const [menuOpen, setMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(layer.name);
  const [invalid, setInvalid] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmReturnFocus, setConfirmReturnFocus] = useState<HTMLButtonElement | null>(null);
  const trigger = useRef<HTMLButtonElement | null>(null);
  const rowRef = useRef<HTMLLIElement | null>(null);
  return (
    <li className="layer-item" data-annotation-layer-id={layer.id} ref={rowRef}>
      <div className={`layer-row annotation-layer-row${selected ? ' layer-row-selected' : ''}`}>
        <span aria-hidden="true" />
        <button
          aria-label={`${layer.visible ? 'Hide' : 'Show'} ${layer.name}`}
          aria-pressed={layer.visible}
          className="icon-button layer-visibility"
          onClick={onToggleVisibility}
          type="button"
        >
          <Icon name={layer.visible ? 'eye' : 'eye-off'} />
        </button>
        <span aria-hidden="true" className="layer-disclosure-spacer" />
        {renaming ? (
          <form
            className="layer-rename-form"
            onSubmit={(event) => {
              event.preventDefault();
              if (onRename(draft)) setRenaming(false);
              else setInvalid(true);
            }}
          >
            <label className="visually-hidden" htmlFor={`rename-${layer.id}`}>
              New annotation layer name
            </label>
            <input
              aria-describedby={invalid ? `rename-${layer.id}-error` : undefined}
              aria-invalid={invalid}
              autoFocus
              id={`rename-${layer.id}`}
              maxLength={120}
              onChange={(event) => {
                setDraft(event.target.value);
                setInvalid(false);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Escape') setRenaming(false);
              }}
              value={draft}
            />
            {invalid ? (
              <span className="visually-hidden" id={`rename-${layer.id}-error`} role="alert">
                Enter a unique, non-empty annotation layer name.
              </span>
            ) : null}
            <button aria-label="Save layer name" className="icon-button" type="submit">
              <Icon name="check" />
            </button>
            <button
              aria-label="Cancel layer rename"
              className="icon-button"
              onClick={() => setRenaming(false)}
              type="button"
            >
              <Icon name="close" />
            </button>
          </form>
        ) : (
          <button
            aria-label={`Select annotation layer ${layer.name}`}
            aria-pressed={selected}
            className="layer-row-main"
            data-list-item-primary="true"
            id={`annotation-layer-select-${layer.id}`}
            onClick={onSelect}
            type="button"
          >
            <span aria-hidden="true" className="annotation-layer-icon">
              {preview ? (
                <AnnotationLegendSymbolPreview symbol={preview} />
              ) : (
                <Icon name="markup" />
              )}
            </span>
            <span className="layer-name" title={layer.name}>
              {layer.name}
            </span>
            <span className="annotation-count">{layer.annotations.length}</span>
          </button>
        )}
        {authoring ? (
          <div className="layer-actions">
            <button
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              aria-label={`Actions for ${layer.name}`}
              className="icon-button layer-actions-trigger"
              onClick={() => setMenuOpen((open) => !open)}
              ref={trigger}
              type="button"
            >
              <Icon name="more" />
            </button>
            <AnchoredMenu
              className="row-actions-menu"
              anchorRef={trigger}
              label={`Actions for ${layer.name}`}
              onDismiss={() => setMenuOpen(false)}
              open={menuOpen}
            >
              <button
                disabled={first}
                onClick={() => {
                  setMenuOpen(false);
                  onMove(-1);
                }}
                role="menuitem"
                type="button"
              >
                <Icon name="arrow-up" />
                Move up
              </button>
              <button
                disabled={last}
                onClick={() => {
                  setMenuOpen(false);
                  onMove(1);
                }}
                role="menuitem"
                type="button"
              >
                <Icon name="arrow-down" />
                Move down
              </button>
              <button
                onClick={() => {
                  setMenuOpen(false);
                  setDraft(layer.name);
                  setRenaming(true);
                }}
                role="menuitem"
                type="button"
              >
                <Icon name="rename" />
                Rename layer
              </button>
              <button
                className="menu-danger"
                onClick={() => {
                  setConfirmReturnFocus(trigger.current);
                  setMenuOpen(false);
                  setConfirming(true);
                }}
                role="menuitem"
                type="button"
              >
                <Icon name="trash" />
                Delete layer
              </button>
            </AnchoredMenu>
          </div>
        ) : null}
      </div>
      {confirming ? (
        <ConfirmationDialog
          confirmLabel="Delete annotation layer"
          confirmTone="danger"
          description={`“${layer.name}” and its ${layer.annotations.length.toLocaleString()} ${layer.annotations.length === 1 ? 'annotation' : 'annotations'} will be deleted.`}
          onCancel={() => setConfirming(false)}
          onConfirm={() => {
            focusAfterListItemRemoval(rowRef.current, onRemove);
            setConfirming(false);
          }}
          returnFocusTo={confirmReturnFocus}
          title="Delete annotation layer?"
        />
      ) : null}
    </li>
  );
}
